import { FlexWrapper, Input, notify } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Avatar } from '@affine/component/ui/avatar';
import { Button } from '@affine/component/ui/button';
import { useSignOut } from '@affine/core/components/hooks/affine/use-sign-out';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import { Upload } from '@affine/core/components/pure/file-upload';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { ArrowRightSmallIcon, CameraIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  AuthService,
  ServerService,
  useCachedOwnAvatarUrl,
} from '../../../../modules/cloud';
import {
  AvatarUploadOverlay,
  useAvatarUploadTask,
} from '../shared/avatar-upload-overlay';
import { SettingsOfflineHeaderAction } from '../shared/settings-offline-status';
import type { SettingState } from '../types';
// sl-y41p: AiProviderPanel removed - replaced by AI Settings tab
import { DataExport } from './data-export';
import { DeleteAccount } from './delete-account';
import { PasskeySection } from './passkey-section';
import { PrivateFolderStorageInfo, StorageProgress } from './storage-progress';
import * as styles from './style.css';
import { TwoFactorSection } from './two-factor-section';

export const UserAvatar = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  const avatarUrl = useCachedOwnAvatarUrl(account);
  const { isAvatarUploading, runAvatarUploadTask } = useAvatarUploadTask();

  const handleUpdateUserAvatar = useCallback(
    (file: File) => {
      void runAvatarUploadTask(async () => {
        try {
          track.$.settingsPanel.accountSettings.uploadAvatar();
          await session.uploadAvatar(file);
          notify.success({
            title: t['com.streamlain.settings.account.avatar.upload-success'](),
          });
        } catch (e) {
          notify.error({
            title: t['com.streamlain.settings.account.avatar.upload-failed'](),
            message: String(e),
          });
        }
      });
    },
    [runAvatarUploadTask, session, t]
  );

  const handleRemoveUserAvatar = useCatchEventCallback(async () => {
    await runAvatarUploadTask(async () => {
      track.$.settingsPanel.accountSettings.removeAvatar();
      await session.removeAvatar();
    });
  }, [runAvatarUploadTask, session]);

  return (
    <Upload
      accept="image/gif,image/jpeg,image/jpg,image/png,image/svg"
      fileChange={handleUpdateUserAvatar}
      data-testid="upload-user-avatar"
      disabled={isAvatarUploading}
    >
      <AvatarUploadOverlay uploading={isAvatarUploading}>
        <Avatar
          size={56}
          name={account?.label}
          url={avatarUrl}
          hoverIcon={!isAvatarUploading ? <CameraIcon /> : undefined}
          onRemove={
            account?.avatar && !isAvatarUploading
              ? handleRemoveUserAvatar
              : undefined
          }
          avatarTooltipOptions={{ content: t['Click to replace photo']() }}
          removeTooltipOptions={{ content: t['Remove photo']() }}
          data-testid="user-setting-avatar"
          removeButtonProps={{
            ['data-testid' as string]: 'user-setting-remove-avatar-button',
          }}
        />
      </AvatarUploadOverlay>
    </Upload>
  );
};

export const AvatarAndName = () => {
  const t = useI18n();
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  const [input, setInput] = useState<string>(account?.label ?? '');

  const allowUpdate = !!input && input !== account?.label;
  const handleUpdateUserName = useAsyncCallback(async () => {
    if (account === null) {
      return;
    }
    if (!allowUpdate) {
      return;
    }

    try {
      track.$.settingsPanel.accountSettings.updateUserName();
      await session.updateLabel(input);
    } catch (e) {
      notify.error({
        title: 'Failed to update user name.',
        message: String(e),
      });
    }
  }, [account, allowUpdate, session, input]);

  return (
    <SettingRow
      name={t['com.streamlain.settings.profile']()}
      desc={t['com.streamlain.settings.profile.message']()}
      spreadCol={false}
    >
      <FlexWrapper style={{ margin: '12px 0 24px 0' }} alignItems="center">
        <UserAvatar />

        <div className={styles.profileInputWrapper}>
          <label>{t['com.streamlain.settings.profile.name']()}</label>
          <FlexWrapper alignItems="center">
            <Input
              defaultValue={input}
              data-testid="user-name-input"
              placeholder={t['com.streamlain.settings.profile.placeholder']()}
              maxLength={64}
              minLength={0}
              style={{ width: 280, height: 32 }}
              onChange={setInput}
              onEnter={handleUpdateUserName}
            />
            {allowUpdate ? (
              <Button
                data-testid="save-user-name"
                onClick={handleUpdateUserName}
                style={{
                  marginLeft: '12px',
                }}
              >
                {t['com.streamlain.editCollection.save']()}
              </Button>
            ) : null}
          </FlexWrapper>
        </div>
      </FlexWrapper>
    </SettingRow>
  );
};

const StoragePanel = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();

  const onUpgrade = useCallback(() => {
    track.$.settingsPanel.accountUsage.viewPlans({
      plan: 'Pro',
    });
    onChangeSettingState?.({
      activeTab: 'workspace:plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  return (
    <SettingRow
      name={t['com.streamlain.workspace.storage']()}
      desc=""
      spreadCol={false}
    >
      <StorageProgress onUpgrade={onUpgrade} />
      <PrivateFolderStorageInfo />
    </SettingRow>
  );
};

export const AccountSetting = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const { authService, serverService, globalDialogService } = useServices({
    AuthService,
    ServerService,
    GlobalDialogService,
  });
  // sl-y41p: serverFeatures no longer needed after AI Provider panel removal
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const session = authService.session;
  useEffect(() => {
    session.revalidate();
  }, [session]);
  const account = useLiveData(session.account$);
  const openSignOutModal = useSignOut();

  const onChangeEmail = useCallback(() => {
    if (!account) {
      return;
    }
    globalDialogService.open('verify-email', {
      server: serverService.server.baseUrl,
      changeEmail: !!account.info?.emailVerified,
    });
  }, [account, globalDialogService, serverService.server.baseUrl]);

  const onPasswordButtonClick = useCallback(() => {
    globalDialogService.open('change-password', {
      server: serverService.server.baseUrl,
    });
  }, [globalDialogService, serverService.server.baseUrl]);

  if (!account) {
    return null;
  }

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.setting.account']()}
        subtitle={t['com.streamlain.setting.account.message']()}
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
        data-testid="account-title"
      />
      <AvatarAndName />
      <SettingWrapper>
        <SettingRow
          name={t['com.streamlain.settings.email']()}
          desc={account.email}
        >
          <Button onClick={onChangeEmail}>
            {account.info?.emailVerified
              ? t['com.streamlain.settings.email.action.change']()
              : t['com.streamlain.settings.email.action.verify']()}
          </Button>
        </SettingRow>
        <SettingRow
          name={t['com.streamlain.settings.password']()}
          desc={t['com.streamlain.settings.password.message']()}
        >
          <Button onClick={onPasswordButtonClick}>
            {account.info?.hasPassword
              ? t['com.streamlain.settings.password.action.change']()
              : t['com.streamlain.settings.password.action.set']()}
          </Button>
        </SettingRow>
        <StoragePanel onChangeSettingState={onChangeSettingState} />
      </SettingWrapper>
      <TwoFactorSection />
      <PasskeySection />
      <SettingWrapper>
        {/* sl-y41p: AI Provider settings moved to dedicated AI Settings tab */}
        <SettingRow
          name={t[`Sign out`]()}
          desc={t['com.streamlain.setting.sign.out.message']()}
          style={{ cursor: 'pointer' }}
          data-testid="sign-out-button"
          onClick={openSignOutModal}
        >
          <ArrowRightSmallIcon />
        </SettingRow>
      </SettingWrapper>
      <DataExport />
      <DeleteAccount onChangeSettingState={onChangeSettingState} />
    </>
  );
};
